/**
 * LaGift — product page
 * ---------------------------------------------------------------------------
 * Joins the parts of the page that the customer experiences as one thing: the
 * picker announces a variant, and this updates the price, the button, the
 * hidden input Dawn's `product-form` posts, and the gallery.
 *
 * It coordinates rather than does. The gallery owns the gallery and the picker
 * owns the picker; this element exists because they sit in different columns
 * and cannot hear each other. `lagift:variant:change` bubbles, so the element
 * wrapping both is the one place that can listen.
 *
 * Nothing is re-fetched. The picker resolved the variant from JSON already in
 * the page, so changing an option costs no request and nothing re-renders.
 */
/**
 * Scoped so the file can run more than once. A `<script src>` executes once per
 * tag, not once per URL, and a top-level class throws "already been declared"
 * on the second run. See CLAUDE.md.
 */
(() => {
  class LagiftMainProduct extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-product-variant-input]');
      this.price = this.querySelector('[data-product-price]');
      this.submit = this.querySelector('[data-product-submit]');
      this.submitLabel = this.querySelector('[data-product-submit-label]');
      this.gallery = this.querySelector('lagift-product-gallery');

      this.addEventListener('lagift:variant:change', (event) => this.onVariantChange(event.detail.variant));
    }

    onVariantChange(variant) {
      if (!variant) {
        // A combination the shop does not sell. The form is disabled rather
        // than left ready to post an id that does not exist.
        this.setUnavailable(this.dataset.labelUnavailable);
        return;
      }

      if (this.input) {
        this.input.value = variant.id;
        // Dawn's product-form re-enables this on submit; a disabled input is
        // not posted, which is what keeps a sold-out variant out of the cart.
        this.input.disabled = !variant.available;
      }

      if (this.price) {
        this.price.innerHTML = variant.onSale
          ? `<span class="lagift-product__price-now">${variant.price}</span><s class="lagift-product__price-was">${variant.compareAtPrice}</s>`
          : `<span class="lagift-product__price-now">${variant.price}</span>`;
      }

      if (this.submit) {
        this.submit.disabled = !variant.available;
        this.submit.removeAttribute('aria-disabled');
      }

      if (this.submitLabel) {
        this.submitLabel.textContent = variant.available
          ? this.dataset.labelAdd || this.submitLabel.textContent
          : this.dataset.labelSoldOut || this.submitLabel.textContent;
      }

      // A variant with its own photograph brings it forward, which is the whole
      // point of choosing a colour. The gallery ignores an id it does not hold.
      if (variant.mediaId) this.gallery?.showMedia(variant.mediaId);
    }

    setUnavailable(label) {
      if (this.input) this.input.disabled = true;
      if (this.submit) this.submit.disabled = true;
      if (this.submitLabel && label) this.submitLabel.textContent = label;
    }
  }

  if (!customElements.get('lagift-main-product')) {
    customElements.define('lagift-main-product', LagiftMainProduct);
  }
})();
